import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';
import axios from 'axios';
import { 
  Calculator, Book, FileText, TrendingUp, Plus, Search, Filter,
  ChevronRight, ChevronDown, DollarSign, CreditCard, Wallet,
  Building, Briefcase, PiggyBank, Receipt, ArrowUpRight, ArrowDownRight,
  Download, BarChart3, PieChart, Activity, RefreshCw, Eye, Edit, Trash2, Pencil} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Account type icons
const accountTypeIcons = {
  asset: Wallet,
  liability: CreditCard,
  equity: Building,
  revenue: ArrowUpRight,
  expense: ArrowDownRight
};

// Account type colors - more subtle
const accountTypeColors = {
  asset: 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20',
  liability: 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20',
  equity: 'text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20',
  revenue: 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20',
  expense: 'text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20'
};

const Accounting = () => {
  const { token } = useAuth();
  const { branding } = useBranding();
  const companyDisplayName = (branding?.company_name || 'PERUSAHAAN').toUpperCase();
  const [activeTab, setActiveTab] = useState('accounts');
  const [accounts, setAccounts] = useState([]);
  const [journals, setJournals] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [trialBalance, setTrialBalance] = useState(null);
  const [incomeStatement, setIncomeStatement] = useState(null);
  const [incomeYearly, setIncomeYearly] = useState(null);
  const [yearlyYear, setYearlyYear] = useState(new Date().getFullYear());
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [balanceYearly, setBalanceYearly] = useState(null);
  const [balanceYear, setBalanceYear] = useState(new Date().getFullYear());
  const [cashFlow, setCashFlow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showCoaModal, setShowCoaModal] = useState(false);
  const [coaForm, setCoaForm] = useState({ code: '', name: '', account_type: 'asset', parent_code: '', _editing_id: null });

  // ===== Tambah Jurnal Manual =====
  const [showJournalModal, setShowJournalModal] = useState(false);
  const [searchJournal, setSearchJournal] = useState('');
  const [journalForm, setJournalForm] = useState({
    entry_date: new Date().toISOString().slice(0, 10),
    description: '',
    reference: '',
    lines: [
      { account_code: '', description: '', debit: 0, credit: 0 },
      { account_code: '', description: '', debit: 0, credit: 0 },
    ],
  });
  const journalTotalDebit = journalForm.lines.reduce((s, l) => s + (Number(l.debit) || 0), 0);
  const journalTotalCredit = journalForm.lines.reduce((s, l) => s + (Number(l.credit) || 0), 0);
  const journalBalanced = Math.abs(journalTotalDebit - journalTotalCredit) < 0.01 && journalTotalDebit > 0;

  const openCreateJournal = () => {
    setJournalForm({
      entry_date: new Date().toISOString().slice(0, 10),
      description: '',
      reference: '',
      lines: [
        { account_code: '', description: '', debit: 0, credit: 0 },
        { account_code: '', description: '', debit: 0, credit: 0 },
      ],
    });
    setShowJournalModal(true);
  };
  const addJournalLine = () => {
    setJournalForm(f => ({ ...f, lines: [...f.lines, { account_code: '', description: '', debit: 0, credit: 0 }] }));
  };
  const removeJournalLine = (idx) => {
    setJournalForm(f => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));
  };
  const updateJournalLine = (idx, field, value) => {
    setJournalForm(f => ({
      ...f,
      lines: f.lines.map((l, i) => i === idx ? { ...l, [field]: value } : l),
    }));
  };
  const saveJournal = async () => {
    if (!journalForm.entry_date || !journalForm.description) {
      showToast('Tanggal dan Deskripsi wajib diisi', 'error');
      return;
    }
    if (!journalBalanced) {
      showToast('Debit dan Kredit harus sama dan tidak nol', 'error');
      return;
    }
    if (journalForm.lines.some(l => !l.account_code)) {
      showToast('Semua baris harus memilih akun', 'error');
      return;
    }
    try {
      const payload = {
        entry_date: journalForm.entry_date,
        description: journalForm.description,
        reference: journalForm.reference || null,
        lines: journalForm.lines.map(l => ({
          account_code: l.account_code,
          description: l.description || journalForm.description,
          debit: Number(l.debit) || 0,
          credit: Number(l.credit) || 0,
        })),
      };
      await axios.post(`${API_URL}/api/v2/accounting/journals`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showToast('Jurnal berhasil dibuat', 'success');
      setShowJournalModal(false);
      fetchJournals();
      fetchAccounts();
    } catch (e) {
      showToast(e?.response?.data?.detail || 'Gagal menyimpan jurnal', 'error');
    }
  };

  const openCreateCoa = (parent) => {
    // Auto-generate next available sub-code under this parent
    let suggestedCode = '';
    if (parent) {
      const prefix = `${parent.code}.`;
      const childPattern = new RegExp(`^${parent.code.replace(/\./g, '\\.')}\\.(\\d+)$`);
      const usedNums = accounts
        .map(a => {
          const m = a.code.match(childPattern);
          return m ? parseInt(m[1], 10) : null;
        })
        .filter(n => n !== null);
      let n = 1;
      while (usedNums.includes(n)) n++;
      suggestedCode = `${prefix}${n}`;
    }
    setCoaForm({
      code: suggestedCode,
      name: '',
      account_type: parent ? parent.account_type : 'asset',
      parent_code: parent ? parent.code : '',
      _editing_id: null,
    });
    setShowCoaModal(true);
  };
  const openEditCoa = (acc) => {
    setCoaForm({
      code: acc.code,
      name: acc.name,
      account_type: acc.account_type,
      parent_code: acc.parent_code || '',
      _editing_id: acc.id,
    });
    setShowCoaModal(true);
  };
  const saveCoa = async () => {
    // Sanitize: strip trailing dots, whitespace
    const cleanCode = String(coaForm.code || '').trim().replace(/\.+$/, '');
    const cleanName = String(coaForm.name || '').trim();
    if (!cleanCode || !cleanName) { showToast('Kode dan Nama akun wajib diisi', 'error'); return; }
    try {
      if (coaForm._editing_id) {
        await axios.put(`${API_URL}/api/v2/finance/accounts/${coaForm._editing_id}`,
          { code: cleanCode, name: cleanName, parent_code: coaForm.parent_code || null },
          { headers: { Authorization: `Bearer ${token}` } });
        showToast('Akun diperbarui', 'success');
      } else {
        await axios.post(`${API_URL}/api/v2/finance/accounts`,
          { code: cleanCode, name: cleanName, account_type: coaForm.account_type, parent_code: coaForm.parent_code || null },
          { headers: { Authorization: `Bearer ${token}` } });
        showToast('Akun dibuat', 'success');
      }
      setShowCoaModal(false);
      fetchAccounts();
    } catch (e) {
      showToast(e?.response?.data?.detail || 'Gagal menyimpan akun', 'error');
    }
  };
  const [showMigrateModal, setShowMigrateModal] = useState(false);
  const [migrateSource, setMigrateSource] = useState(null);
  const [migrateTargetId, setMigrateTargetId] = useState('');

  const deleteCoa = async (acc) => {
    if (!window.confirm(`Hapus akun ${acc.code} ${acc.name}?`)) return;
    try {
      await axios.delete(`${API_URL}/api/v2/finance/accounts/${acc.id}`, { headers: { Authorization: `Bearer ${token}` } });
      showToast('Akun dihapus', 'success');
      fetchAccounts();
    } catch (e) {
      const detail = e?.response?.data?.detail || '';
      if (detail.includes('jurnal') || detail.includes('transaksi')) {
        // Open migrate-and-delete modal
        setMigrateSource(acc);
        setMigrateTargetId('');
        setShowMigrateModal(true);
      } else {
        showToast(detail || 'Gagal menghapus akun', 'error');
      }
    }
  };

  const runMigrateDelete = async () => {
    if (!migrateTargetId) { showToast('Pilih akun tujuan terlebih dahulu', 'error'); return; }
    try {
      const res = await axios.post(`${API_URL}/api/v2/finance/accounts/${migrateSource.id}/migrate-and-delete`,
        { target_account_id: Number(migrateTargetId) },
        { headers: { Authorization: `Bearer ${token}` } });
      showToast(`${res.data?.migrated_lines || 0} transaksi dipindahkan, akun dihapus`, 'success');
      setShowMigrateModal(false);
      fetchAccounts();
    } catch (e) {
      showToast(e?.response?.data?.detail || 'Migrasi gagal', 'error');
    }
  };
  const [ledgerPeriod, setLedgerPeriod] = useState('yearly'); // 'monthly' | 'yearly' | 'all'
  const [ledgerYear, setLedgerYear] = useState(new Date().getFullYear());
  const [ledgerMonth, setLedgerMonth] = useState(new Date().getMonth() + 1);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  
  // Edit Journal State
  const [editingJournal, setEditingJournal] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    transaction_date: '',
    description: '',
    reference: '',
    lines: []
  });
  const [saving, setSaving] = useState(false);
  
  // Toast notification state
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  
  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState({ show: false, journal: null });
  
  // Toast helper function
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  useEffect(() => {
    if (token) {
      initializeAccounting();
      fetchAccounts();
    }
  }, [token]);

  useEffect(() => {
    if (activeTab === 'journals' && token) {
      fetchJournals();
    } else if (activeTab === 'ledger' && token) {
      fetchLedger();
    } else if (activeTab === 'trial-balance' && token) {
      fetchTrialBalance();
    } else if (activeTab === 'income-statement' && token) {
      fetchIncomeYearly(yearlyYear);
      fetchIncomeStatement();
    } else if (activeTab === 'balance-sheet' && token) {
      fetchBalanceSheet();
      fetchBalanceYearly(balanceYear);
    } else if (activeTab === 'cash-flow' && token) {
      fetchCashFlow();
    }
  }, [activeTab, token, dateRange, ledgerPeriod, ledgerYear, ledgerMonth]);

  const initializeAccounting = async () => {
    // V2 MySQL version doesn't need initialization - accounts are pre-seeded
    console.log('Using MySQL accounting - no initialization needed');
  };

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/v2/finance/accounts?include_headers=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setAccounts(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchJournals = async () => {
    try {
      setLoading(true);
      const { start, end } = getLedgerDateRange();
      const params = new URLSearchParams();
      if (start) params.set('start_date', start);
      if (end) params.set('end_date', end);
      params.set('limit', '500');
      const qs = params.toString() ? `?${params.toString()}` : '';
      const response = await axios.get(`${API_URL}/api/v2/accounting/journals${qs}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setJournals(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching journals:', error);
    } finally {
      setLoading(false);
    }
  };

  const getLedgerDateRange = () => {
    if (ledgerPeriod === 'monthly') {
      const y = ledgerYear; const m = ledgerMonth;
      const last = new Date(y, m, 0).getDate();
      const pad = (n) => String(n).padStart(2, '0');
      return { start: `${y}-${pad(m)}-01`, end: `${y}-${pad(m)}-${pad(last)}` };
    }
    if (ledgerPeriod === 'yearly') {
      return { start: `${ledgerYear}-01-01`, end: `${ledgerYear}-12-31` };
    }
    // 'all' - kosongkan, biarkan backend pakai default
    return { start: '', end: '' };
  };

  const fetchLedger = async (accountCode = null) => {
    try {
      setLoading(true);
      if (accountCode) {
        const { start, end } = getLedgerDateRange();
        const params = new URLSearchParams();
        if (start) params.set('start_date', start);
        if (end) params.set('end_date', end);
        const qs = params.toString() ? `?${params.toString()}` : '';
        const response = await axios.get(`${API_URL}/api/v2/finance/ledger/${accountCode}${qs}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success) {
          setLedger(response.data.data);
        }
      } else {
        // No account selected - show empty
        setLedger({ entries: [] });
      }
    } catch (error) {
      console.error('Error fetching ledger:', error);
      setLedger({ entries: [] });
    } finally {
      setLoading(false);
    }
  };

  const fetchTrialBalance = async () => {
    try {
      setLoading(true);
      const { end } = getLedgerDateRange();
      const qs = end ? `?as_of_date=${end}` : '';
      const response = await axios.get(`${API_URL}/api/v2/accounting/reports/trial-balance${qs}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setTrialBalance(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching trial balance:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchIncomeStatement = async () => {
    try {
      setLoading(true);
      // Use MySQL-based endpoint (v2) for accurate reporting
      console.log('Fetching income statement from v2 endpoint...');
      const response = await axios.get(
        `${API_URL}/api/v2/accounting/reports/income-statement?start_date=${dateRange.start}&end_date=${dateRange.end}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('V2 Response:', response.data);
      if (response.data.success) {
        setIncomeStatement(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching income statement from v2:', error.response?.data || error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchIncomeYearly = async (year) => {
    try {
      setLoading(true);
      const yr = year || yearlyYear;
      const response = await axios.get(
        `${API_URL}/api/v2/accounting/reports/income-statement-yearly?year=${yr}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setIncomeYearly(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching income yearly:', error.response?.data || error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchBalanceSheet = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/api/v2/accounting/reports/balance-sheet?as_of_date=${dateRange.end}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setBalanceSheet(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching balance sheet:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBalanceYearly = async (year) => {
    try {
      setLoading(true);
      const yr = year || balanceYear;
      const response = await axios.get(
        `${API_URL}/api/v2/accounting/reports/balance-sheet-yearly?year=${yr}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setBalanceYearly(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching balance yearly:', error.response?.data || error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCashFlow = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/v2/accounting/reports/cash-flow`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          start_date: dateRange.start,
          end_date: dateRange.end
        }
      });
      if (response.data.success) {
        setCashFlow(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching cash flow:', error);
      // Set empty data on error
      setCashFlow({
        beginning_cash: 0,
        ending_cash: 0,
        operating: { items: [], inflow: 0, outflow: 0, total: 0 },
        investing: { items: [], inflow: 0, outflow: 0, total: 0 },
        financing: { items: [], inflow: 0, outflow: 0, total: 0 },
        net_change_in_cash: 0,
        cash_accounts: []
      });
    } finally {
      setLoading(false);
    }
  };

  // ==========================================
  // EXPORT FUNCTIONS
  // ==========================================
  
  const exportReport = async (reportType, format = 'excel') => {
    try {
      let endpoint = '';
      let filename = '';
      
      switch(reportType) {
        case 'general-ledger':
          {
            const { start: _ls, end: _le } = getLedgerDateRange();
            const _qsParts = [`account_code=${encodeURIComponent(selectedAccount?.code || '')}`, `format=${format}`];
            if (_ls) _qsParts.push(`start_date=${_ls}`);
            if (_le) _qsParts.push(`end_date=${_le}`);
            endpoint = `/api/v2/accounting/export/general-ledger?${_qsParts.join('&')}`;
          }
          filename = `buku_besar_${selectedAccount?.code || ''}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
          break;
        case 'chart-of-accounts':
          endpoint = `/api/v2/accounting/export/chart-of-accounts?format=${format}`;
          filename = `daftar_akun.${format === 'excel' ? 'xlsx' : 'pdf'}`;
          break;
        case 'trial-balance':
          {
            const { end: _tbe } = getLedgerDateRange();
            endpoint = `/api/v2/accounting/export/trial-balance?format=${format}` + (_tbe ? `&as_of_date=${_tbe}` : '');
          }
          filename = `neraca_saldo.${format === 'excel' ? 'xlsx' : 'pdf'}`;
          break;
        case 'income-statement':
          endpoint = `/api/v2/accounting/export/income-statement?format=${format}&year=${yearlyYear}`;
          filename = `laba_rugi_${yearlyYear}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
          break;
        case 'balance-sheet':
          endpoint = `/api/v2/accounting/export/balance-sheet?format=${format}&year=${balanceYear}`;
          filename = `neraca_${balanceYear}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
          break;
        case 'cash-flow':
          endpoint = `/api/v2/accounting/export/cash-flow?format=${format}&start_date=${dateRange.start}&end_date=${dateRange.end}`;
          filename = `arus_kas_${dateRange.start}_${dateRange.end}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
          break;
        case 'journal':
          {
            const { start: _js, end: _je } = getLedgerDateRange();
            const _qs = [`format=${format}`];
            if (_js) _qs.push(`start_date=${_js}`);
            if (_je) _qs.push(`end_date=${_je}`);
            endpoint = `/api/v2/accounting/export/journal?${_qs.join('&')}`;
          }
          {
            const { start: _jfs, end: _jfe } = getLedgerDateRange();
            filename = `jurnal_${_jfs || 'awal'}_${_jfe || 'akhir'}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
          }
          break;
        default:
          showToast('Tipe laporan tidak valid', 'error');
          return;
      }
      
      showToast(`Mengunduh ${reportType.replace('-', ' ')}...`, 'info');
      
      const response = await axios.get(`${API_URL}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      showToast('Laporan berhasil diunduh!', 'success');
    } catch (error) {
      console.error('Export error:', error);
      showToast('Gagal mengunduh laporan', 'error');
    }
  };

  const exportToCSV = async (type) => {
    // Redirect to new export function with excel format
    exportReport(type === 'journals' ? 'journal' : 'trial-balance', 'excel');
  };

  // ==========================================
  // SYNC EXPENSE JOURNALS
  // ==========================================
  
  const syncExpenseJournals = async () => {
    try {
      const currentMonth = new Date().getMonth() + 1;
      const currentYear = new Date().getFullYear();
      
      showToast('Menyinkronkan jurnal pengeluaran...', 'info');
      
      const response = await axios.post(
        `${API_URL}/api/v2/accounting/sync-expense-journals?month=${currentMonth}&year=${currentYear}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        const data = response.data.data;
        showToast(
          `Berhasil sync ${data.total_categories} kategori, total ${formatCurrency(data.total_amount)}`,
          'success'
        );
        fetchJournals();
        fetchTrialBalance();
        fetchBalanceSheet();
      }
    } catch (error) {
      console.error('Sync error:', error);
      showToast(error.response?.data?.detail || 'Gagal sync jurnal', 'error');
    }
  };

  // ==========================================
  // EDIT & DELETE JOURNAL FUNCTIONS
  // ==========================================
  
  const handleEditJournal = (journal) => {
    setEditingJournal(journal);
    setEditForm({
      transaction_date: journal.entry_date?.split('T')[0] || '',
      description: journal.description || '',
      reference: journal.reference || '',
      lines: journal.lines?.map(line => ({
        account_id: line.account_id,
        account_code: line.account_code,
        account_name: line.account_name,
        description: line.description || '',
        debit: line.debit || 0,
        credit: line.credit || 0
      })) || []
    });
    setShowEditModal(true);
  };

  const handleSaveJournal = async () => {
    if (!editingJournal) return;
    
    // Validate balance
    const totalDebit = editForm.lines.reduce((sum, l) => sum + (parseFloat(l.debit) || 0), 0);
    const totalCredit = editForm.lines.reduce((sum, l) => sum + (parseFloat(l.credit) || 0), 0);
    
    if (Math.abs(totalDebit - totalCredit) > 0.01) {
      showToast('Jurnal harus seimbang! Total Debit harus sama dengan Total Kredit', 'error');
      return;
    }
    
    try {
      setSaving(true);
      const response = await axios.put(
        `${API_URL}/api/v2/accounting/journals/${editingJournal.id}`,
        {
          transaction_date: editForm.transaction_date,
          description: editForm.description,
          reference: editForm.reference,
          lines: editForm.lines.map(line => ({
            account_id: line.account_id,
            description: line.description,
            debit: parseFloat(line.debit) || 0,
            credit: parseFloat(line.credit) || 0
          }))
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setShowEditModal(false);
        setEditingJournal(null);
        fetchJournals();
        fetchAccounts(); // Refresh account balances
        showToast('Jurnal berhasil diperbarui!', 'success');
      }
    } catch (error) {
      console.error('Error updating journal:', error);
      showToast('Gagal memperbarui jurnal: ' + (error.response?.data?.detail || error.message), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteJournal = async (journal) => {
    setConfirmDialog({ show: true, journal });
  };
  
  const confirmDeleteJournal = async () => {
    const journal = confirmDialog.journal;
    if (!journal) return;
    
    try {
      const response = await axios.delete(
        `${API_URL}/api/v2/accounting/journals/${journal.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        fetchJournals();
        fetchAccounts(); // Refresh account balances
        showToast('Jurnal berhasil dihapus!', 'success');
      }
    } catch (error) {
      console.error('Error deleting journal:', error);
      showToast('Gagal menghapus jurnal: ' + (error.response?.data?.detail || error.message), 'error');
    } finally {
      setConfirmDialog({ show: false, journal: null });
    }
  };

  const updateLineAmount = (lineIndex, field, value) => {
    const newLines = [...editForm.lines];
    newLines[lineIndex][field] = parseFloat(value) || 0;
    setEditForm({ ...editForm, lines: newLines });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const groupAccountsByType = () => {
    const grouped = { asset: [], liability: [], equity: [], revenue: [], expense: [] };
    accounts.forEach(acc => {
      if (grouped[acc.account_type]) grouped[acc.account_type].push(acc);
    });
    return grouped;
  };

  const filteredAccounts = accounts.filter(acc => 
    acc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    acc.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Compute indentation level by walking parent_code chain
  const accountLevel = (acc) => {
    if (!acc.parent_code) return 0;
    let lvl = 0, p = acc.parent_code;
    const byCode = Object.fromEntries(accounts.map(a => [a.code, a]));
    while (p && lvl < 6) {
      lvl += 1;
      p = byCode[p]?.parent_code || null;
    }
    return lvl;
  };

  // ================== RENDER FUNCTIONS ==================

  const renderAccountsTab = () => {
    const grouped = groupAccountsByType();
    const labels = { asset: 'Aset', liability: 'Kewajiban', equity: 'Modal', revenue: 'Pendapatan', expense: 'Beban' };
    
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 space-y-4">
        {/* Summary Cards - Compact */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {Object.entries(grouped).map(([type, accs]) => {
            const Icon = accountTypeIcons[type];
            const total = accs.reduce((sum, acc) => sum + (acc.current_balance || 0), 0);
            
            return (
              <div key={type} className={`p-3 rounded-lg border ${accountTypeColors[type]} border-current/20`}>
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4" />
                  <span className="text-xs font-medium">{labels[type]}</span>
                </div>
                <p className="text-sm font-bold mt-1">{formatCurrency(total)}</p>
              </div>
            );
          })}
        </div>

        {/* Search and Actions */}
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input 
              placeholder="Cari akun..." 
              value={searchTerm} 
              onChange={(e) => setSearchTerm(e.target.value)} 
              className="pl-8 h-9 text-sm" 
            />
          </div>
          <Button variant="outline" size="sm" onClick={() => exportReport('chart-of-accounts', 'excel')} className="gap-1.5 h-9" data-testid="btn-export-coa-excel">
            <Download className="w-3.5 h-3.5" /> Excel
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportReport('chart-of-accounts', 'pdf')} className="gap-1.5 h-9" data-testid="btn-export-coa-pdf">
            <Download className="w-3.5 h-3.5" /> PDF
          </Button>
          <Button size="sm" onClick={() => openCreateCoa(null)} className="gap-1.5 h-9 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="btn-add-coa">
            <Plus className="w-3.5 h-3.5" /> Tambah Akun
          </Button>
        </div>

        {/* Accounts Table - Compact */}
        <div className="border rounded-lg overflow-x-auto">
          <table className="min-w-[640px] w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">Kode</th>
                <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">Nama Akun</th>
                <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300 hidden sm:table-cell">Tipe</th>
                <th className="text-right px-3 py-2 font-medium text-gray-600 dark:text-gray-300">Saldo</th>
                <th className="text-center px-3 py-2 font-medium text-gray-600 dark:text-gray-300 w-32">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {filteredAccounts.map((acc) => {
                const Icon = accountTypeIcons[acc.account_type] || Calculator;
                const colorClass = accountTypeColors[acc.account_type] || 'text-gray-600 bg-gray-50';
                const isHeader = acc.is_header;
                const lvl = accountLevel(acc);

                return (
                  <tr
                    key={acc.id}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-800/50 ${isHeader ? 'bg-gray-50/50 dark:bg-gray-800/30' : ''}`}
                  >
                    <td className="px-3 py-2 font-mono text-xs cursor-pointer" onClick={() => { setSelectedAccount(acc); setActiveTab('ledger'); fetchLedger(acc.id); }}>{acc.code}</td>
                    <td className="px-3 py-2 cursor-pointer" onClick={() => { setSelectedAccount(acc); setActiveTab('ledger'); fetchLedger(acc.id); }}>
                      <div className="flex items-center gap-2" style={{ paddingLeft: `${lvl * 16}px` }}>
                        {lvl > 0 && <span className="text-gray-400 text-xs">↳</span>}
                        <Icon className={`w-3.5 h-3.5 ${colorClass.split(' ')[0]}`} />
                        <span className={isHeader ? 'font-medium' : ''}>{acc.name}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 hidden sm:table-cell">
                      <span className={`px-2 py-0.5 rounded text-xs ${colorClass}`}>
                        {labels[acc.account_type]}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-xs cursor-pointer" onClick={() => { setSelectedAccount(acc); setActiveTab('ledger'); fetchLedger(acc.id); }}>
                      {!isHeader && formatCurrency(acc.current_balance)}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="inline-flex gap-1">
                        <button title="Tambah Sub-Akun" onClick={(e) => { e.stopPropagation(); openCreateCoa(acc); }}
                          className="p-1 rounded text-indigo-600 hover:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-indigo-900/30" data-testid={`btn-add-sub-${acc.code}`}>
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                        {!acc.is_system && (
                          <>
                            <button title="Edit" onClick={(e) => { e.stopPropagation(); openEditCoa(acc); }}
                              className="p-1 rounded text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/30" data-testid={`btn-edit-coa-${acc.code}`}>
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button title="Hapus" onClick={(e) => { e.stopPropagation(); deleteCoa(acc); }}
                              className="p-1 rounded text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30" data-testid={`btn-del-coa-${acc.code}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderJournalsTab = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <h3 className="font-medium">Jurnal Umum</h3>
        <div className="flex gap-2 flex-wrap items-center">
          <div className="relative">
            <input
              type="text"
              value={searchJournal}
              onChange={(e) => setSearchJournal(e.target.value)}
              placeholder="Cari no/keterangan/nominal..."
              className="text-xs border rounded-lg pl-7 pr-2 py-1.5 bg-white dark:bg-gray-800 h-8 w-56"
              data-testid="input-journal-search"
            />
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
            {searchJournal && (
              <button onClick={() => setSearchJournal('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500 text-xs">x</button>
            )}
          </div>
          <select
            className="text-xs border rounded-lg px-2 py-1.5 bg-white dark:bg-gray-800 h-8"
            value={ledgerPeriod}
            onChange={(e) => setLedgerPeriod(e.target.value)}
            data-testid="select-journal-period"
          >
            <option value="monthly">Bulanan</option>
            <option value="yearly">Tahunan</option>
            <option value="all">Semua</option>
          </select>
          {ledgerPeriod === 'monthly' && (
            <select
              className="text-xs border rounded-lg px-2 py-1.5 bg-white dark:bg-gray-800 h-8"
              value={ledgerMonth}
              onChange={(e) => setLedgerMonth(parseInt(e.target.value))}
              data-testid="select-journal-month"
            ><option value="1">Januari</option><option value="2">Februari</option><option value="3">Maret</option><option value="4">April</option><option value="5">Mei</option><option value="6">Juni</option><option value="7">Juli</option><option value="8">Agustus</option><option value="9">September</option><option value="10">Oktober</option><option value="11">November</option><option value="12">Desember</option></select>
          )}
          {ledgerPeriod !== 'all' && (
            <select
              className="text-xs border rounded-lg px-2 py-1.5 bg-white dark:bg-gray-800 h-8"
              value={ledgerYear}
              onChange={(e) => setLedgerYear(parseInt(e.target.value))}
              data-testid="select-journal-year"
            >
              {Array.from({length: 5}, (_, i) => new Date().getFullYear() - i).map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          )}
          <Button variant="outline" size="sm" onClick={syncExpenseJournals} className="gap-1.5 h-8 text-orange-600 border-orange-300 hover:bg-orange-50 dark:text-orange-400 dark:hover:bg-orange-900/30">
            <RefreshCw className="w-3.5 h-3.5" /> Sync Pengeluaran
          </Button>
          <Button size="sm" onClick={openCreateJournal} className="gap-1.5 h-8 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="btn-add-journal">
            <Plus className="w-3.5 h-3.5" /> Tambah Jurnal
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportReport('journal', 'excel')} className="gap-1.5 h-8">
            <Download className="w-3.5 h-3.5" /> Excel
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportReport('journal', 'pdf')} className="gap-1.5 h-8">
            <FileText className="w-3.5 h-3.5" /> PDF
          </Button>
        </div>
      </div>
      
      {/* 2026-05-29: Mobile card view (sm:hidden) — replaces unreadable horizontal-scroll table on phone */}
      <div className="sm:hidden space-y-2">
        {journals.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500 border rounded-lg dark:text-slate-400">Belum ada jurnal</div>
        ) : (
          journals.filter((j) => {
            const q = searchJournal.trim().toLowerCase();
            if (!q) return true;
            return (String(j.entry_number||'').toLowerCase().includes(q) ||
                    String(j.description||'').toLowerCase().includes(q) ||
                    String(j.account_name||'').toLowerCase().includes(q));
          }).map((j) => (
            <div key={'mob-'+j.id} className="border rounded-lg p-3 bg-white dark:bg-gray-800 shadow-sm">
              <div className="flex items-center justify-between mb-1.5">
                <span className="font-mono text-[11px] text-indigo-600 font-semibold dark:text-indigo-400">{j.entry_number || '-'}</span>
                <span className="text-[10px] text-gray-500 dark:text-slate-400">{j.entry_date ? new Date(j.entry_date).toLocaleDateString('id-ID') : '-'}</span>
              </div>
              <p className="text-xs text-gray-800 dark:text-gray-200 mb-2">{j.description}</p>
              <div className="grid grid-cols-2 gap-2 text-[11px]">
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded px-2 py-1">
                  <div className="text-gray-500 dark:text-slate-400">Debit</div>
                  <div className="font-mono font-semibold text-blue-700 dark:text-blue-300">{formatCurrency(j.total_debit)}</div>
                </div>
                <div className="bg-red-50 dark:bg-red-900/20 rounded px-2 py-1">
                  <div className="text-gray-500 dark:text-slate-400">Kredit</div>
                  <div className="font-mono font-semibold text-red-700 dark:text-red-300">{formatCurrency(j.total_credit)}</div>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-2 pt-2 border-t">
                <button onClick={() => handleEditJournal(j)} className="text-[11px] px-2 py-1 text-blue-600 bg-blue-50 rounded dark:text-blue-400 dark:bg-blue-900/30" data-testid={'mob-edit-jurnal-'+j.id}>Edit</button>
                <button onClick={() => handleDeleteJournal(j)} className="text-[11px] px-2 py-1 text-red-600 bg-red-50 rounded dark:text-red-400 dark:bg-red-900/30" data-testid={'mob-del-jurnal-'+j.id}>Hapus</button>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="hidden sm:block border rounded-lg overflow-x-auto">
        <table className="min-w-[640px] w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="text-left px-3 py-2 font-medium">No. Jurnal</th>
              <th className="text-left px-3 py-2 font-medium hidden sm:table-cell">Tanggal</th>
              <th className="text-left px-3 py-2 font-medium">Keterangan</th>
              <th className="text-right px-3 py-2 font-medium">Debit</th>
              <th className="text-right px-3 py-2 font-medium">Kredit</th>
              <th className="text-center px-3 py-2 font-medium w-20">Aksi</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {journals.length === 0 ? (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-500 dark:text-slate-400">Belum ada jurnal</td></tr>
            ) : (
              (journals.filter((j) => {
                const q = searchJournal.trim().toLowerCase();
                if (!q) return true;
                const num = String(j.entry_number || '').toLowerCase();
                const desc = String(j.description || '').toLowerCase();
                const acct = String(j.account_name || '').toLowerCase();
                const ref = String(j.reference || '').toLowerCase();
                const debit = String(j.total_debit || j.debit || 0);
                const credit = String(j.total_credit || j.credit || 0);
                return num.includes(q) || desc.includes(q) || acct.includes(q) || ref.includes(q) || debit.includes(q) || credit.includes(q);
              })).map((j) => (
                <tr key={j.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-3 py-2 font-mono text-xs">{j.entry_number || '-'}</td>
                  <td className="px-3 py-2 text-xs hidden sm:table-cell">
                    {j.entry_date ? new Date(j.entry_date).toLocaleDateString('id-ID') : '-'}
                  </td>
                  <td className="px-3 py-2 text-xs">{j.description}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs text-blue-600 dark:text-blue-400">{formatCurrency(j.total_debit)}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs text-red-600 dark:text-red-400">{formatCurrency(j.total_credit)}</td>
                  <td className="px-3 py-2">
                    <div className="flex justify-center gap-1">
                      <button 
                        onClick={() => handleEditJournal(j)}
                        className="p-1 text-blue-600 hover:bg-blue-50 rounded dark:text-blue-400 dark:hover:bg-blue-900/30"
                        title="Edit Jurnal"
                      >
                        <Edit className="w-3.5 h-3.5" />
                      </button>
                      <button 
                        onClick={() => handleDeleteJournal(j)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded dark:text-red-400 dark:hover:bg-red-900/30"
                        title="Hapus Jurnal"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderLedgerTab = () => {
    const entries = ledger?.entries || [];
    return (
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row justify-between gap-2">
          <div>
            <h3 className="font-medium">Buku Besar</h3>
            {selectedAccount && <p className="text-xs text-gray-500 dark:text-slate-400">{selectedAccount.code} - {selectedAccount.name}</p>}
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <select
              className="text-xs border rounded-lg px-2 py-1.5 bg-white dark:bg-gray-800 h-8"
              value={ledgerPeriod}
              onChange={(e) => { setLedgerPeriod(e.target.value); if (selectedAccount) setTimeout(() => fetchLedger(selectedAccount.code), 0); }}
              data-testid="select-ledger-period"
            >
              <option value="monthly">Bulanan</option>
              <option value="yearly">Tahunan</option>
              <option value="all">Semua</option>
            </select>
            {ledgerPeriod === 'monthly' && (
              <select
                className="text-xs border rounded-lg px-2 py-1.5 bg-white dark:bg-gray-800 h-8"
                value={ledgerMonth}
                onChange={(e) => { setLedgerMonth(parseInt(e.target.value)); if (selectedAccount) setTimeout(() => fetchLedger(selectedAccount.code), 0); }}
                data-testid="select-ledger-month"
              ><option value="1">Januari</option><option value="2">Februari</option><option value="3">Maret</option><option value="4">April</option><option value="5">Mei</option><option value="6">Juni</option><option value="7">Juli</option><option value="8">Agustus</option><option value="9">September</option><option value="10">Oktober</option><option value="11">November</option><option value="12">Desember</option></select>
            )}
            {ledgerPeriod !== 'all' && (
              <select
                className="text-xs border rounded-lg px-2 py-1.5 bg-white dark:bg-gray-800 h-8"
                value={ledgerYear}
                onChange={(e) => { setLedgerYear(parseInt(e.target.value)); if (selectedAccount) setTimeout(() => fetchLedger(selectedAccount.code), 0); }}
                data-testid="select-ledger-year"
              >
                {Array.from({length: 5}, (_, i) => new Date().getFullYear() - i).map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            )}
            {selectedAccount && (
              <>
                <Button variant="outline" size="sm" onClick={() => exportReport('general-ledger', 'excel')} className="gap-1.5 h-8" data-testid="btn-export-ledger-excel">
                  <Download className="w-3.5 h-3.5" /> Excel
                </Button>
                <Button variant="outline" size="sm" onClick={() => exportReport('general-ledger', 'pdf')} className="gap-1.5 h-8" data-testid="btn-export-ledger-pdf">
                  <FileText className="w-3.5 h-3.5" /> PDF
                </Button>
              </>
            )}
          <select 
            className="text-sm border rounded-lg px-3 py-1.5 bg-white dark:bg-gray-800"
            value={selectedAccount?.code || ''}
            onChange={(e) => { 
              const acc = accounts.find(a => a.code === e.target.value); 
              setSelectedAccount(acc); 
              if (acc) fetchLedger(acc.code); 
            }}
          >
            <option value="">Pilih Akun</option>
            {accounts.map(acc => (
              <option key={acc.id} value={acc.code}>{acc.code} - {acc.name}</option>
            ))}
          </select>
          </div>
        </div>

        {ledger?.account && (
          <div className="grid grid-cols-4 gap-2">
            <div className="p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <p className="text-[10px] text-gray-500 dark:text-slate-400">Saldo Awal</p>
              <p className="text-sm font-semibold">{formatCurrency(ledger.account.opening_balance)}</p>
            </div>
            <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <p className="text-[10px] text-blue-600 dark:text-blue-400">Total Debit</p>
              <p className="text-sm font-semibold text-blue-600 dark:text-blue-400">{formatCurrency(entries.reduce((sum, e) => sum + (e.debit || 0), 0))}</p>
            </div>
            <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded-lg">
              <p className="text-[10px] text-red-600 dark:text-red-400">Total Kredit</p>
              <p className="text-sm font-semibold text-red-600 dark:text-red-400">{formatCurrency(entries.reduce((sum, e) => sum + (e.credit || 0), 0))}</p>
            </div>
            <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <p className="text-[10px] text-green-600 dark:text-green-400">Saldo Akhir</p>
              <p className="text-sm font-semibold text-green-600 dark:text-green-400">{formatCurrency(ledger.account.current_balance)}</p>
            </div>
          </div>
        )}

        <div className="border rounded-lg overflow-x-auto">
          <table className="min-w-[640px] w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Tanggal</th>
                <th className="text-left px-3 py-2 font-medium hidden sm:table-cell">No. Jurnal</th>
                <th className="text-left px-3 py-2 font-medium">Keterangan</th>
                <th className="text-right px-3 py-2 font-medium">Debit</th>
                <th className="text-right px-3 py-2 font-medium">Kredit</th>
                <th className="text-right px-3 py-2 font-medium">Saldo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {entries.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-500 dark:text-slate-400">
                  {selectedAccount ? 'Belum ada transaksi' : 'Pilih akun'}
                </td></tr>
              ) : (
                entries.map((e) => (
                  <tr key={e.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-3 py-2 text-xs">{e.entry_date ? new Date(e.entry_date).toLocaleDateString('id-ID') : '-'}</td>
                    <td className="px-3 py-2 font-mono text-xs hidden sm:table-cell">{e.entry_number || '-'}</td>
                    <td className="px-3 py-2 text-xs">{e.description || '-'}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs text-blue-600 dark:text-blue-400">{e.debit > 0 ? formatCurrency(e.debit) : '-'}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs text-red-600 dark:text-red-400">{e.credit > 0 ? formatCurrency(e.credit) : '-'}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs font-medium">{formatCurrency(e.balance)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderTrialBalanceTab = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <h3 className="font-medium">Neraca Saldo</h3>
        <div className="flex gap-2 flex-wrap items-center">
          <select
            className="text-xs border rounded-lg px-2 py-1.5 bg-white dark:bg-gray-800 h-8"
            value={ledgerPeriod}
            onChange={(e) => setLedgerPeriod(e.target.value)}
            data-testid="select-tb-period"
          >
            <option value="monthly">Bulanan</option>
            <option value="yearly">Tahunan</option>
            <option value="all">Semua</option>
          </select>
          {ledgerPeriod === 'monthly' && (
            <select
              className="text-xs border rounded-lg px-2 py-1.5 bg-white dark:bg-gray-800 h-8"
              value={ledgerMonth}
              onChange={(e) => setLedgerMonth(parseInt(e.target.value))}
              data-testid="select-tb-month"
            ><option value="1">Januari</option><option value="2">Februari</option><option value="3">Maret</option><option value="4">April</option><option value="5">Mei</option><option value="6">Juni</option><option value="7">Juli</option><option value="8">Agustus</option><option value="9">September</option><option value="10">Oktober</option><option value="11">November</option><option value="12">Desember</option></select>
          )}
          {ledgerPeriod !== 'all' && (
            <select
              className="text-xs border rounded-lg px-2 py-1.5 bg-white dark:bg-gray-800 h-8"
              value={ledgerYear}
              onChange={(e) => setLedgerYear(parseInt(e.target.value))}
              data-testid="select-tb-year"
            >
              {Array.from({length: 5}, (_, i) => new Date().getFullYear() - i).map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          )}
          <Button variant="outline" size="sm" onClick={() => exportReport('trial-balance', 'excel')} className="gap-1.5 h-8">
            <Download className="w-3.5 h-3.5" /> Excel
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportReport('trial-balance', 'pdf')} className="gap-1.5 h-8">
            <FileText className="w-3.5 h-3.5" /> PDF
          </Button>
          <Button variant="outline" size="sm" onClick={fetchTrialBalance} className="gap-1.5 h-8">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </Button>
        </div>
      </div>

      {trialBalance && (
        <>
          <div className={`px-3 py-2 rounded-lg text-sm flex items-center gap-2 ${
            trialBalance.totals?.is_balanced ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}>
            {trialBalance.totals?.is_balanced ? '✓ Neraca Saldo Seimbang' : '✗ Neraca Saldo Tidak Seimbang'}
          </div>

          {/* 2026-05-29: Mobile card view for Neraca Saldo */}
          <div className="sm:hidden space-y-2">
            {(trialBalance.accounts || []).map((acc, idx) => (
              <div key={'mob-tb-'+idx} className="border rounded-lg p-2.5 bg-white dark:bg-gray-800 shadow-sm">
                <div className="flex items-baseline justify-between mb-1.5">
                  <span className="font-mono text-[11px] text-indigo-600 font-semibold dark:text-indigo-400">{acc.code}</span>
                </div>
                <p className="text-xs font-medium text-gray-800 dark:text-gray-200 mb-2 leading-snug">{acc.name}</p>
                <div className="grid grid-cols-2 gap-2 text-[11px]">
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded px-2 py-1">
                    <div className="text-gray-500 dark:text-slate-400">Debit</div>
                    <div className="font-mono font-semibold text-blue-700 dark:text-blue-300">{acc.debit > 0 ? formatCurrency(acc.debit) : '-'}</div>
                  </div>
                  <div className="bg-red-50 dark:bg-red-900/20 rounded px-2 py-1">
                    <div className="text-gray-500 dark:text-slate-400">Kredit</div>
                    <div className="font-mono font-semibold text-red-700 dark:text-red-300">{acc.credit > 0 ? formatCurrency(acc.credit) : '-'}</div>
                  </div>
                </div>
              </div>
            ))}
            <div className="border-2 border-indigo-500 rounded-lg p-3 bg-indigo-50 dark:bg-indigo-900/20">
              <div className="text-xs font-bold text-gray-700 mb-2 dark:text-slate-200">TOTAL</div>
              <div className="grid grid-cols-2 gap-2 text-[11px]">
                <div><div className="text-gray-500 dark:text-slate-400">Debit</div><div className="font-mono font-bold text-blue-700 dark:text-blue-300">{formatCurrency(trialBalance.totals?.debit || 0)}</div></div>
                <div><div className="text-gray-500 dark:text-slate-400">Kredit</div><div className="font-mono font-bold text-red-700 dark:text-red-300">{formatCurrency(trialBalance.totals?.credit || 0)}</div></div>
              </div>
            </div>
          </div>

          <div className="hidden sm:block border rounded-lg overflow-x-auto">
            <table className="min-w-[640px] w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Kode</th>
                  <th className="text-left px-3 py-2 font-medium">Nama Akun</th>
                  <th className="text-right px-3 py-2 font-medium">Debit</th>
                  <th className="text-right px-3 py-2 font-medium">Kredit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {(trialBalance.accounts || []).map((acc, idx) => (
                  <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-3 py-2 font-mono text-xs">{acc.code}</td>
                    <td className="px-3 py-2 text-xs">{acc.name}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs text-blue-600 dark:text-blue-400">{acc.debit > 0 ? formatCurrency(acc.debit) : '-'}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs text-red-600 dark:text-red-400">{acc.credit > 0 ? formatCurrency(acc.credit) : '-'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-100 dark:bg-gray-800 font-semibold">
                <tr>
                  <td colSpan={2} className="px-3 py-2 text-sm">TOTAL</td>
                  <td className="px-3 py-2 text-right font-mono text-sm text-blue-700 dark:text-blue-300">{formatCurrency(trialBalance.totals?.debit || 0)}</td>
                  <td className="px-3 py-2 text-right font-mono text-sm text-red-700 dark:text-red-300">{formatCurrency(trialBalance.totals?.credit || 0)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </div>
  );

  const renderIncomeStatementTab = () => {
    const monthLabels = ['Jan','Feb','Mar','Apr','Mei','Jun','Jul','Agu','Sep','Okt','Nov','Des'];
    const fmt = (n) => {
      if (!n || n === 0) return '-';
      return new Intl.NumberFormat('id-ID', { maximumFractionDigits: 0 }).format(n);
    };
    const yearOptions = [];
    const cy = new Date().getFullYear();
    for (let y = cy + 1; y >= 2023; y--) yearOptions.push(y);

    return (
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2 pb-2 border-b">
          <h3 className="font-medium text-sm">Laporan Laba Rugi {incomeYearly?.year || yearlyYear}</h3>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-gray-500 hidden sm:inline dark:text-slate-400">Tahun:</span>
            <select
              value={yearlyYear}
              onChange={(e) => { const y = parseInt(e.target.value); setYearlyYear(y); fetchIncomeYearly(y); }}
              className="h-7 px-2 text-xs border rounded"
              data-testid="laba-rugi-year-select"
            >
              {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <Button variant="outline" size="sm" onClick={() => exportReport('income-statement', 'excel')} className="gap-1 h-7 px-2 ml-1" data-testid="laba-rugi-export-excel">
              <Download className="w-3 h-3" /> Excel
            </Button>
            <Button variant="outline" size="sm" onClick={() => exportReport('income-statement', 'pdf')} className="gap-1 h-7 px-2" data-testid="laba-rugi-export-pdf">
              <FileText className="w-3 h-3" /> PDF
            </Button>
          </div>
        </div>

        {!incomeYearly ? (
          <div className="text-center text-xs text-gray-500 py-8 dark:text-slate-400">Memuat data...</div>
        ) : (
          <>
            {/* Matrix Akun x Bulan */}
            <div className="border rounded-lg overflow-x-auto bg-white dark:bg-gray-900">
              <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800 border-b">
                <div className="text-center font-semibold text-sm">LAPORAN LABA RUGI</div>
                <div className="text-center text-xs text-gray-500 dark:text-slate-400">{companyDisplayName} - Tahun {incomeYearly.year}</div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-[640px] w-full text-[11px]" data-testid="laba-rugi-table">
                  <thead className="bg-gray-100 dark:bg-gray-800">
                    <tr>
                      <th className="px-2 py-1.5 border text-left w-20">No. Akun</th>
                      <th className="px-2 py-1.5 border text-left min-w-[200px]">URAIAN</th>
                      {monthLabels.map((m, i) => (
                        <th key={i} className="px-1.5 py-1.5 border text-right">{m}</th>
                      ))}
                      <th className="px-2 py-1.5 border text-right bg-blue-50 dark:bg-blue-900/30">Jumlah</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* PENDAPATAN */}
                    <tr className="bg-green-50 dark:bg-green-900/20 font-semibold">
                      <td colSpan={15} className="px-2 py-1 border">PENDAPATAN</td>
                    </tr>
                    {incomeYearly.revenues.map((r, idx) => (
                      <tr key={`r-${idx}`} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                        <td className="px-2 py-1 border font-mono">{r.code}</td>
                        <td className="px-2 py-1 border">{r.name}</td>
                        {r.monthly.map((v, mi) => (
                          <td key={mi} className="px-1.5 py-1 border text-right font-mono">{fmt(v)}</td>
                        ))}
                        <td className="px-2 py-1 border text-right font-mono font-semibold bg-blue-50 dark:bg-blue-900/20">{fmt(r.total)}</td>
                      </tr>
                    ))}
                    <tr className="bg-green-100 dark:bg-green-900/40 font-semibold">
                      <td className="px-2 py-1 border"></td>
                      <td className="px-2 py-1 border">Total Pendapatan</td>
                      {incomeYearly.monthly_revenue_total.map((v, mi) => (
                        <td key={mi} className="px-1.5 py-1 border text-right font-mono">{fmt(v)}</td>
                      ))}
                      <td className="px-2 py-1 border text-right font-mono">{fmt(incomeYearly.total_revenue)}</td>
                    </tr>

                    {/* BEBAN */}
                    <tr className="bg-orange-50 dark:bg-orange-900/20 font-semibold">
                      <td colSpan={15} className="px-2 py-1 border">BIAYA / BEBAN</td>
                    </tr>
                    {incomeYearly.expenses.map((e, idx) => (
                      <tr key={`e-${idx}`} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                        <td className="px-2 py-1 border font-mono">{e.code}</td>
                        <td className="px-2 py-1 border">{e.name}</td>
                        {e.monthly.map((v, mi) => (
                          <td key={mi} className="px-1.5 py-1 border text-right font-mono">{fmt(v)}</td>
                        ))}
                        <td className="px-2 py-1 border text-right font-mono font-semibold bg-blue-50 dark:bg-blue-900/20">{fmt(e.total)}</td>
                      </tr>
                    ))}
                    <tr className="bg-orange-100 dark:bg-orange-900/40 font-semibold">
                      <td className="px-2 py-1 border"></td>
                      <td className="px-2 py-1 border">Jumlah Biaya</td>
                      {incomeYearly.monthly_expense_total.map((v, mi) => (
                        <td key={mi} className="px-1.5 py-1 border text-right font-mono">{fmt(v)}</td>
                      ))}
                      <td className="px-2 py-1 border text-right font-mono">{fmt(incomeYearly.total_expense)}</td>
                    </tr>

                    {/* LABA / RUGI */}
                    <tr className="bg-blue-100 dark:bg-blue-900/40 font-bold border-t-2">
                      <td className="px-2 py-1.5 border"></td>
                      <td className="px-2 py-1.5 border">LABA (RUGI)</td>
                      {incomeYearly.monthly_net.map((v, mi) => (
                        <td key={mi} className={`px-1.5 py-1.5 border text-right font-mono ${v < 0 ? 'text-red-600' : 'text-blue-700'}`}>{fmt(v)}</td>
                      ))}
                      <td className={`px-2 py-1.5 border text-right font-mono ${incomeYearly.net_income < 0 ? 'text-red-600' : 'text-blue-700'}`}>{fmt(incomeYearly.net_income)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Ringkasan per bulan */}
            <div className="border rounded-lg overflow-x-auto bg-white dark:bg-gray-900">
              <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800 border-b text-center font-semibold text-sm">
                Ringkasan Per Bulan
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-[640px] w-full text-[11px]">
                  <thead className="bg-gray-100 dark:bg-gray-800">
                    <tr>
                      <th className="px-2 py-1.5 border text-left">Bulan</th>
                      <th className="px-2 py-1.5 border text-right">Pendapatan</th>
                      <th className="px-2 py-1.5 border text-right">Biaya</th>
                      <th className="px-2 py-1.5 border text-right">Laba (Rugi)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthLabels.map((m, i) => (
                      <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                        <td className="px-2 py-1 border">{m} {incomeYearly.year}</td>
                        <td className="px-2 py-1 border text-right font-mono">{fmt(incomeYearly.monthly_revenue_total[i])}</td>
                        <td className="px-2 py-1 border text-right font-mono">{fmt(incomeYearly.monthly_expense_total[i])}</td>
                        <td className={`px-2 py-1 border text-right font-mono font-semibold ${incomeYearly.monthly_net[i] < 0 ? 'text-red-600' : 'text-blue-700'}`}>{fmt(incomeYearly.monthly_net[i])}</td>
                      </tr>
                    ))}
                    <tr className="bg-blue-50 dark:bg-blue-900/30 font-bold border-t-2">
                      <td className="px-2 py-1.5 border">Total Tahun {incomeYearly.year}</td>
                      <td className="px-2 py-1.5 border text-right font-mono">{fmt(incomeYearly.total_revenue)}</td>
                      <td className="px-2 py-1.5 border text-right font-mono">{fmt(incomeYearly.total_expense)}</td>
                      <td className={`px-2 py-1.5 border text-right font-mono ${incomeYearly.net_income < 0 ? 'text-red-600' : 'text-blue-700'}`}>{fmt(incomeYearly.net_income)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderBalanceSheetTab = () => {
    const fmt = (n) => {
      if (n === null || n === undefined) return '-';
      const num = Number(n);
      if (num === 0) return '0';
      return new Intl.NumberFormat('id-ID', { maximumFractionDigits: 0 }).format(num);
    };
    const yearOptions = [];
    const cy = new Date().getFullYear();
    for (let y = cy + 1; y >= 2023; y--) yearOptions.push(y);

    return (
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2 pb-2 border-b">
          <h3 className="font-medium text-sm">Laporan Posisi Keuangan (Neraca)</h3>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-gray-500 hidden sm:inline dark:text-slate-400">Per 31 Desember:</span>
            <select
              value={balanceYear}
              onChange={(e) => { const y = parseInt(e.target.value); setBalanceYear(y); fetchBalanceYearly(y); }}
              className="h-7 px-2 text-xs border rounded"
              data-testid="neraca-year-select"
            >
              {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <Button variant="outline" size="sm" onClick={() => exportReport('balance-sheet', 'excel')} className="gap-1 h-7 px-2 ml-1" data-testid="neraca-export-excel">
              <Download className="w-3 h-3" /> Excel
            </Button>
            <Button variant="outline" size="sm" onClick={() => exportReport('balance-sheet', 'pdf')} className="gap-1 h-7 px-2" data-testid="neraca-export-pdf">
              <FileText className="w-3 h-3" /> PDF
            </Button>
          </div>
        </div>

        {!balanceYearly ? (
          <div className="text-center text-xs text-gray-500 py-8 dark:text-slate-400">Memuat data...</div>
        ) : (
          <div className="border rounded-lg overflow-x-auto bg-white dark:bg-gray-900" data-testid="neraca-table">
            <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800 border-b text-center">
              <div className="font-bold text-sm">{companyDisplayName}</div>
              <div className="font-semibold text-xs">LAPORAN POSISI KEUANGAN (NERACA)</div>
              <div className="text-xs text-gray-600 dark:text-slate-300">PER 31 DESEMBER {balanceYearly.year}</div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-[640px] w-full text-xs border-collapse">
                <thead className="bg-gray-100 dark:bg-gray-800 border-b">
                  <tr>
                    <th className="px-3 py-2 border text-left font-semibold w-1/4">ASET (AKTIVA)</th>
                    <th className="px-3 py-2 border text-right font-semibold w-1/4">NILAI (Rp)</th>
                    <th className="px-3 py-2 border text-left font-semibold w-1/4">KEWAJIBAN & EKUITAS (PASIVA)</th>
                    <th className="px-3 py-2 border text-right font-semibold w-1/4">NILAI (Rp)</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Section 1: Aset Lancar | Kewajiban */}
                  <tr className="bg-blue-50 dark:bg-blue-900/20 font-semibold">
                    <td className="px-3 py-1.5 border" colSpan={2}>I. ASET LANCAR</td>
                    <td className="px-3 py-1.5 border" colSpan={2}>I. KEWAJIBAN (HUTANG)</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-1.5 border pl-6">Kas dan Setara Kas</td>
                    <td className="px-3 py-1.5 border text-right font-mono">{fmt(balanceYearly.aset_lancar.kas_setara_kas)}</td>
                    <td className="px-3 py-1.5 border pl-6">Hutang Usaha</td>
                    <td className="px-3 py-1.5 border text-right font-mono">{fmt(balanceYearly.kewajiban.hutang_usaha)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-1.5 border pl-6">Piutang Usaha</td>
                    <td className="px-3 py-1.5 border text-right font-mono">{fmt(balanceYearly.aset_lancar.piutang_usaha)}</td>
                    <td className="px-3 py-1.5 border pl-6">Hutang Pajak</td>
                    <td className="px-3 py-1.5 border text-right font-mono">{fmt(balanceYearly.kewajiban.hutang_pajak)}</td>
                  </tr>
                  <tr className="bg-blue-50 dark:bg-blue-900/20 font-semibold">
                    <td className="px-3 py-1.5 border">Total Aset Lancar</td>
                    <td className="px-3 py-1.5 border text-right font-mono">{fmt(balanceYearly.aset_lancar.total)}</td>
                    <td className="px-3 py-1.5 border">Total Kewajiban</td>
                    <td className="px-3 py-1.5 border text-right font-mono">{fmt(balanceYearly.kewajiban.total)}</td>
                  </tr>
                  <tr><td className="px-3 py-2 border" colSpan={4}>&nbsp;</td></tr>

                  {/* Section 2: Aset Tidak Lancar | Ekuitas */}
                  <tr className="bg-green-50 dark:bg-green-900/20 font-semibold">
                    <td className="px-3 py-1.5 border" colSpan={2}>II. ASET TIDAK LANCAR</td>
                    <td className="px-3 py-1.5 border" colSpan={2}>II. EKUITAS (MODAL)</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-1.5 border pl-6">Aset Tetap</td>
                    <td className="px-3 py-1.5 border text-right font-mono">{fmt(balanceYearly.aset_tidak_lancar.aset_tetap)}</td>
                    <td className="px-3 py-1.5 border pl-6">Modal Saham</td>
                    <td className="px-3 py-1.5 border text-right font-mono">{fmt(balanceYearly.ekuitas.modal_saham)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-1.5 border pl-6">Akumulasi Penyusutan</td>
                    <td className="px-3 py-1.5 border text-right font-mono text-red-600 dark:text-red-400">{fmt(balanceYearly.aset_tidak_lancar.akumulasi_penyusutan)}</td>
                    <td className="px-3 py-1.5 border pl-6">Laba Tahun Berjalan</td>
                    <td className={`px-3 py-1.5 border text-right font-mono ${balanceYearly.ekuitas.laba_tahun_berjalan < 0 ? 'text-red-600' : ''}`}>{fmt(balanceYearly.ekuitas.laba_tahun_berjalan)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-1.5 border"></td>
                    <td className="px-3 py-1.5 border"></td>
                    <td className="px-3 py-1.5 border pl-6">Laba Ditahan</td>
                    <td className="px-3 py-1.5 border text-right font-mono">{fmt(balanceYearly.ekuitas.laba_ditahan)}</td>
                  </tr>
                  <tr className="bg-green-50 dark:bg-green-900/20 font-semibold">
                    <td className="px-3 py-1.5 border">Total Aset Tidak Lancar</td>
                    <td className="px-3 py-1.5 border text-right font-mono">{fmt(balanceYearly.aset_tidak_lancar.total)}</td>
                    <td className="px-3 py-1.5 border">Total Ekuitas</td>
                    <td className="px-3 py-1.5 border text-right font-mono">{fmt(balanceYearly.ekuitas.total)}</td>
                  </tr>
                  <tr><td className="px-3 py-2 border" colSpan={4}>&nbsp;</td></tr>

                  {/* Total */}
                  <tr className="bg-blue-100 dark:bg-blue-900/40 font-bold border-t-2">
                    <td className="px-3 py-2 border">TOTAL ASET</td>
                    <td className="px-3 py-2 border text-right font-mono">{fmt(balanceYearly.total_aset)}</td>
                    <td className="px-3 py-2 border">TOTAL KEWAJIBAN & EKUITAS</td>
                    <td className="px-3 py-2 border text-right font-mono">{fmt(balanceYearly.total_pasiva)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className={`px-3 py-2 text-xs border-t ${balanceYearly.balanced ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400' : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'}`}>
              {balanceYearly.balanced
                ? '✓ Neraca BALANCED - Total Aset = Total Pasiva'
                : `⚠ Neraca TIDAK SEIMBANG - selisih ${fmt(balanceYearly.total_aset - balanceYearly.total_pasiva)}`}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderCashFlowTab = () => (
    <div className="space-y-3">
      {/* Header dengan date picker compact */}
      <div className="flex flex-wrap items-center justify-between gap-2 pb-2 border-b">
        <h3 className="font-medium text-sm">Laporan Arus Kas</h3>
        <div className="flex items-center gap-1 text-xs">
          <span className="text-gray-500 hidden sm:inline dark:text-slate-400">Periode:</span>
          <input 
            type="date" 
            value={dateRange.start} 
            onChange={(e) => setDateRange(prev => ({...prev, start: e.target.value}))} 
            className="h-7 px-2 text-xs border rounded w-28"
          />
          <span className="text-gray-400">s/d</span>
          <input 
            type="date" 
            value={dateRange.end} 
            onChange={(e) => setDateRange(prev => ({...prev, end: e.target.value}))} 
            className="h-7 px-2 text-xs border rounded w-28"
          />
          <Button variant="outline" size="sm" onClick={() => exportReport('cash-flow', 'excel')} className="gap-1 h-7 px-2 ml-1">
            <Download className="w-3 h-3" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportReport('cash-flow', 'pdf')} className="gap-1 h-7 px-2">
            <FileText className="w-3 h-3" />
          </Button>
        </div>
      </div>
      
      {cashFlow && (
        <div className="space-y-3">
          {/* Summary Cards - Compact */}
          <div className="grid grid-cols-2 gap-2">
            <div className="p-2 bg-gray-50 dark:bg-gray-800 rounded border">
              <p className="text-[10px] text-gray-500 uppercase dark:text-slate-400">Kas Awal</p>
              <p className="text-sm font-bold">{formatCurrency(cashFlow.beginning_cash)}</p>
            </div>
            <div className="p-2 bg-blue-500 text-white rounded">
              <p className="text-[10px] opacity-80 uppercase">Kas Akhir</p>
              <p className="text-sm font-bold">{formatCurrency(cashFlow.ending_cash)}</p>
            </div>
          </div>

          {/* Activities - Compact */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {[
              { key: 'operating', title: 'Operasi', color: 'bg-blue-500' },
              { key: 'investing', title: 'Investasi', color: 'bg-purple-500' },
              { key: 'financing', title: 'Pendanaan', color: 'bg-orange-500' }
            ].map(({ key, title, color }) => (
              <div key={key} className="border rounded-lg overflow-x-auto">
                <div className={`px-2 py-1.5 ${color} text-white text-xs font-medium flex justify-between`}>
                  <span>{title}</span>
                  <span className={cashFlow[key]?.total >= 0 ? '' : 'text-red-200'}>
                    {formatCurrency(cashFlow[key]?.total || 0)}
                  </span>
                </div>
                {cashFlow[key]?.items?.length > 0 ? (
                  <div className="divide-y divide-gray-100 dark:divide-gray-700 max-h-32 overflow-y-auto">
                    {cashFlow[key].items.slice(0, 5).map((item, idx) => (
                      <div key={idx} className="flex justify-between px-2 py-1 text-[10px]">
                        <span className="truncate flex-1">{item.name?.substring(0, 25) || '-'}</span>
                        <span className={`font-mono ml-1 ${item.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(item.amount)}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="px-2 py-2 text-center text-[10px] text-gray-400">Tidak ada data</div>
                )}
              </div>
            ))}
          </div>

          {/* Net Change - Compact */}
          <div className={`px-3 py-2 rounded-lg flex justify-between items-center ${
            cashFlow.net_change_in_cash >= 0 ? 'bg-green-500' : 'bg-red-500'
          } text-white`}>
            <span className="text-sm font-medium">Perubahan Kas Bersih</span>
            <span className="text-lg font-bold">{formatCurrency(cashFlow.net_change_in_cash)}</span>
          </div>
        </div>
      )}
    </div>
  );

  // ================== MAIN RENDER ==================

  if (loading && accounts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
        <p className="text-sm text-gray-500 dark:text-slate-400">Memuat data...</p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Toast Notification */}
      {toast.show && (
        <div className="fixed top-4 right-4 z-[100] animate-in slide-in-from-top-2 fade-in duration-300">
          <div className={`flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg border ${
            toast.type === 'success' 
              ? 'bg-green-50 border-green-200 text-green-800' 
              : 'bg-red-50 border-red-200 text-red-800'
          }`}>
            <div className={`p-1 rounded-full ${toast.type === 'success' ? 'bg-green-100' : 'bg-red-100'}`}>
              {toast.type === 'success' ? (
                <TrendingUp className="w-4 h-4 text-green-600 dark:text-green-400" />
              ) : (
                <Activity className="w-4 h-4 text-red-600 dark:text-red-400" />
              )}
            </div>
            <span className="text-sm font-medium">{toast.message}</span>
            <button 
              onClick={() => setToast({ ...toast, show: false })}
              className="ml-2 p-1 hover:bg-black/5 rounded"
            >
              <ChevronRight className="w-3.5 h-3.5 rotate-45" />
            </button>
          </div>
        </div>
      )}

      {/* Confirm Delete Journal Dialog */}
      {confirmDialog.show && confirmDialog.journal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-sm overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-red-100 rounded-full dark:bg-red-900/40">
                  <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Hapus Jurnal</h3>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                Apakah Anda yakin ingin menghapus jurnal ini?
              </p>
              <p className="text-xs text-gray-500 mb-3 dark:text-slate-400">
                Ini akan menghapus jurnal dan mengembalikan saldo akun terkait.
              </p>
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{confirmDialog.journal.journal_number}</p>
                <p className="text-xs text-gray-500 dark:text-slate-400">{confirmDialog.journal.description}</p>
              </div>
            </div>
            <div className="px-4 py-3 border-t bg-gray-50 dark:bg-gray-800 flex justify-end gap-2">
              <button 
                onClick={() => setConfirmDialog({ show: false, journal: null })}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors dark:text-slate-200 dark:bg-slate-800 dark:border-slate-600 dark:hover:bg-slate-700/50"
              >
                Batal
              </button>
              <button 
                onClick={confirmDeleteJournal}
                className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors"
              >
                Ya, Hapus
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header - Compact */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-blue-500 rounded-lg">
          <Calculator className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-lg font-bold text-gray-800 dark:text-gray-100">Akuntansi</h1>
          <p className="text-xs text-gray-500 dark:text-slate-400">Kelola keuangan dan pembukuan perusahaan</p>
        </div>
      </div>

      {/* Tabs - Compact */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap gap-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg h-auto">
          <TabsTrigger value="accounts" className="text-xs px-2 py-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm rounded">
            <Book className="w-3.5 h-3.5 mr-1" /> Daftar Akun
          </TabsTrigger>
          <TabsTrigger value="journals" className="text-xs px-2 py-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm rounded">
            <FileText className="w-3.5 h-3.5 mr-1" /> Jurnal
          </TabsTrigger>
          <TabsTrigger value="ledger" className="text-xs px-2 py-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm rounded">
            <Book className="w-3.5 h-3.5 mr-1" /> Buku Besar
          </TabsTrigger>
          <TabsTrigger value="trial-balance" className="text-xs px-2 py-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm rounded">
            <BarChart3 className="w-3.5 h-3.5 mr-1" /> Neraca Saldo
          </TabsTrigger>
          <TabsTrigger value="income-statement" className="text-xs px-2 py-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm rounded">
            <TrendingUp className="w-3.5 h-3.5 mr-1" /> Laba Rugi
          </TabsTrigger>
          <TabsTrigger value="balance-sheet" className="text-xs px-2 py-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm rounded">
            <PieChart className="w-3.5 h-3.5 mr-1" /> Neraca
          </TabsTrigger>
          <TabsTrigger value="cash-flow" className="text-xs px-2 py-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm rounded">
            <Activity className="w-3.5 h-3.5 mr-1" /> Arus Kas
          </TabsTrigger>
        </TabsList>

        <div className="mt-4">
          <TabsContent value="accounts" className="mt-0">{renderAccountsTab()}</TabsContent>
          <TabsContent value="journals" className="mt-0">{renderJournalsTab()}</TabsContent>
          <TabsContent value="ledger" className="mt-0">{renderLedgerTab()}</TabsContent>
          <TabsContent value="trial-balance" className="mt-0">{renderTrialBalanceTab()}</TabsContent>
          <TabsContent value="income-statement" className="mt-0">{renderIncomeStatementTab()}</TabsContent>
          <TabsContent value="balance-sheet" className="mt-0">{renderBalanceSheetTab()}</TabsContent>
          <TabsContent value="cash-flow" className="mt-0">{renderCashFlowTab()}</TabsContent>
        </div>
      </Tabs>

      {/* Add Journal Modal */}
      {showJournalModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3 overflow-y-auto" data-testid="journal-modal">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-3xl my-6">
            <div className="px-4 py-3 border-b flex justify-between items-center bg-indigo-600 text-white rounded-t-lg sticky top-0 z-10">
              <h3 className="text-sm font-semibold">Tambah Jurnal Manual</h3>
              <button onClick={() => setShowJournalModal(false)} className="text-white/90 hover:text-white text-xl leading-none">×</button>
            </div>
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1 dark:text-slate-300">Tanggal *</label>
                  <input type="date" value={journalForm.entry_date} onChange={(e) => setJournalForm(f => ({...f, entry_date: e.target.value}))}
                    className="w-full h-9 text-sm border rounded px-2" data-testid="journal-form-date" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1 dark:text-slate-300">Referensi (opsional)</label>
                  <input value={journalForm.reference} onChange={(e) => setJournalForm(f => ({...f, reference: e.target.value}))}
                    placeholder="Mis. INV-001, KW-2026-005" className="w-full h-9 text-sm border rounded px-2" data-testid="journal-form-ref" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1 dark:text-slate-300">Deskripsi *</label>
                <input value={journalForm.description} onChange={(e) => setJournalForm(f => ({...f, description: e.target.value}))}
                  placeholder="Mis. Setor modal ke Bank Mandiri" className="w-full h-9 text-sm border rounded px-2" data-testid="journal-form-desc" />
              </div>

              <div className="border rounded">
                <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800 border-b text-xs font-semibold flex justify-between">
                  <span>Baris Jurnal (minimal 2)</span>
                  <button type="button" onClick={addJournalLine} className="text-indigo-600 hover:underline dark:text-indigo-400" data-testid="journal-add-line">+ Tambah Baris</button>
                </div>
                <div className="divide-y">
                  {journalForm.lines.map((line, idx) => (
                    <div key={idx} className="px-3 py-2 grid grid-cols-12 gap-2 items-start text-xs">
                      <div className="col-span-5">
                        <select value={line.account_code} onChange={(e) => updateJournalLine(idx, 'account_code', e.target.value)}
                          className="w-full h-8 border rounded px-1 text-xs" data-testid={`journal-line-${idx}-account`}>
                          <option value="">— Pilih Akun —</option>
                          {accounts.filter(a => !a.is_header).map(a => (
                            <option key={a.id} value={a.code}>{a.code} — {a.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-span-3">
                        <input type="number" min="0" step="1" value={line.debit} onChange={(e) => {
                          const v = Number(e.target.value) || 0;
                          updateJournalLine(idx, 'debit', v);
                          if (v > 0) updateJournalLine(idx, 'credit', 0);
                        }} placeholder="Debit" className="w-full h-8 border rounded px-2 text-right text-xs" data-testid={`journal-line-${idx}-debit`} />
                      </div>
                      <div className="col-span-3">
                        <input type="number" min="0" step="1" value={line.credit} onChange={(e) => {
                          const v = Number(e.target.value) || 0;
                          updateJournalLine(idx, 'credit', v);
                          if (v > 0) updateJournalLine(idx, 'debit', 0);
                        }} placeholder="Kredit" className="w-full h-8 border rounded px-2 text-right text-xs" data-testid={`journal-line-${idx}-credit`} />
                      </div>
                      <div className="col-span-1 text-center">
                        {journalForm.lines.length > 2 && (
                          <button type="button" onClick={() => removeJournalLine(idx)} className="p-1 text-red-600 hover:bg-red-50 rounded dark:text-red-400 dark:hover:bg-red-900/30" title="Hapus baris">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800 border-t grid grid-cols-12 gap-2 text-xs font-semibold">
                  <div className="col-span-5 text-right">Total:</div>
                  <div className="col-span-3 text-right">{formatCurrency(journalTotalDebit)}</div>
                  <div className="col-span-3 text-right">{formatCurrency(journalTotalCredit)}</div>
                  <div className="col-span-1"></div>
                </div>
                <div className={`px-3 py-2 text-xs text-center font-medium ${journalBalanced ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                  {journalBalanced ? '✅ Seimbang' : `⚠️ Selisih: ${formatCurrency(Math.abs(journalTotalDebit - journalTotalCredit))}`}
                </div>
              </div>
            </div>
            <div className="px-4 py-3 border-t flex justify-end gap-2 bg-gray-50 dark:bg-gray-800 rounded-b-lg sticky bottom-0">
              <Button variant="outline" size="sm" onClick={() => setShowJournalModal(false)} data-testid="journal-form-cancel">Batal</Button>
              <Button size="sm" onClick={saveJournal} disabled={!journalBalanced} className="bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50" data-testid="journal-form-save">Simpan Jurnal</Button>
            </div>
          </div>
        </div>
      )}

      {/* Migrate-and-Delete Modal */}
      {showMigrateModal && migrateSource && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3" data-testid="migrate-modal">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-md">
            <div className="px-4 py-3 border-b flex justify-between items-center bg-amber-600 text-white rounded-t-lg">
              <h3 className="text-sm font-semibold">Pindahkan Transaksi & Hapus</h3>
              <button onClick={() => setShowMigrateModal(false)} className="text-white/90 hover:text-white text-xl leading-none">×</button>
            </div>
            <div className="p-4 space-y-3 text-sm">
              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-800 dark:bg-amber-900/30">
                Akun <b>{migrateSource.code} — {migrateSource.name}</b> memiliki transaksi. Pindahkan dulu semua jurnalnya ke akun lain, lalu akun ini akan dihapus.
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1 dark:text-slate-300">Pindahkan transaksi ke akun: *</label>
                <select value={migrateTargetId} onChange={(e) => setMigrateTargetId(e.target.value)}
                  className="w-full h-9 text-sm border rounded px-2" data-testid="migrate-target-select">
                  <option value="">— Pilih akun tujuan ({migrateSource.account_type}) —</option>
                  {accounts
                    .filter(a => a.account_type === migrateSource.account_type && a.id !== migrateSource.id && !a.is_header)
                    .map(a => (
                      <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
                    ))}
                </select>
                <p className="text-[10px] text-gray-500 mt-1 dark:text-slate-400">Hanya akun dengan tipe sama yang ditampilkan.</p>
              </div>
            </div>
            <div className="px-4 py-3 border-t flex justify-end gap-2 bg-gray-50 dark:bg-gray-800 rounded-b-lg">
              <Button variant="outline" size="sm" onClick={() => setShowMigrateModal(false)}>Batal</Button>
              <Button size="sm" onClick={runMigrateDelete} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="migrate-confirm-btn">
                Pindahkan & Hapus
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* COA Add/Edit Modal */}
      {showCoaModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3" data-testid="coa-modal">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-md">
            <div className="px-4 py-3 border-b flex justify-between items-center bg-indigo-600 text-white rounded-t-lg">
              <h3 className="text-sm font-semibold">{coaForm._editing_id ? 'Edit Akun' : 'Tambah Akun'}{coaForm.parent_code ? ` di bawah ${coaForm.parent_code}` : ''}</h3>
              <button onClick={() => setShowCoaModal(false)} className="text-white/90 hover:text-white text-xl leading-none">×</button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1 dark:text-slate-300">Kode Akun *</label>
                <input value={coaForm.code} onChange={(e) => setCoaForm({...coaForm, code: e.target.value})}
                  className="w-full h-9 text-sm border rounded px-2 font-mono" placeholder="contoh: 1.11.1.1" data-testid="coa-form-code" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1 dark:text-slate-300">Nama Akun *</label>
                <input value={coaForm.name} onChange={(e) => setCoaForm({...coaForm, name: e.target.value})}
                  className="w-full h-9 text-sm border rounded px-2" placeholder="contoh: Bank BCA" data-testid="coa-form-name" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1 dark:text-slate-300">Tipe Akun *</label>
                <select value={coaForm.account_type} onChange={(e) => setCoaForm({...coaForm, account_type: e.target.value})}
                  disabled={!!coaForm._editing_id || !!coaForm.parent_code}
                  className="w-full h-9 text-sm border rounded px-2 disabled:bg-gray-100" data-testid="coa-form-type">
                  <option value="asset">Aset</option>
                  <option value="liability">Kewajiban</option>
                  <option value="equity">Modal</option>
                  <option value="revenue">Pendapatan</option>
                  <option value="expense">Beban</option>
                </select>
                {coaForm.parent_code && (<p className="text-[10px] text-gray-500 mt-1 dark:text-slate-400">Tipe akan mengikuti induk: {coaForm.parent_code}</p>)}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1 dark:text-slate-300">Akun Induk (opsional)</label>
                <select value={coaForm.parent_code} onChange={(e) => {
                  const pc = e.target.value;
                  const p = accounts.find(a => a.code === pc);
                  setCoaForm({...coaForm, parent_code: pc, account_type: p ? p.account_type : coaForm.account_type });
                }} className="w-full h-9 text-sm border rounded px-2" data-testid="coa-form-parent">
                  <option value="">— Akun Utama (tanpa induk) —</option>
                  {accounts.map(a => (
                    <option key={a.id} value={a.code}>{a.code} — {a.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="px-4 py-3 border-t flex justify-end gap-2 bg-gray-50 dark:bg-gray-800 rounded-b-lg">
              <Button variant="outline" size="sm" onClick={() => setShowCoaModal(false)} data-testid="coa-form-cancel">Batal</Button>
              <Button size="sm" onClick={saveCoa} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="coa-form-save">Simpan</Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Journal Modal - Compact */}
      {showEditModal && editingJournal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-lg max-h-[85vh] overflow-hidden">
            {/* Modal Header - Compact */}
            <div className="px-3 py-2 border-b flex justify-between items-center bg-blue-500 text-white">
              <h3 className="text-sm font-medium">
                ✏️ Edit: {editingJournal.journal_number}
              </h3>
              <button 
                onClick={() => { setShowEditModal(false); setEditingJournal(null); }}
                className="text-white/80 hover:text-white text-lg leading-none"
              >
                ×
              </button>
            </div>

            {/* Modal Body - Compact */}
            <div className="p-3 overflow-y-auto max-h-[55vh] space-y-3">
              {/* Basic Info - Single Row */}
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-0.5 dark:text-slate-400">Tanggal</label>
                  <input 
                    type="date" 
                    value={editForm.transaction_date}
                    onChange={(e) => setEditForm({ ...editForm, transaction_date: e.target.value })}
                    className="h-7 w-full text-xs border rounded px-2"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-0.5 dark:text-slate-400">Referensi</label>
                  <input 
                    value={editForm.reference || ''}
                    onChange={(e) => setEditForm({ ...editForm, reference: e.target.value })}
                    className="h-7 w-full text-xs border rounded px-2"
                    placeholder="No. Bukti"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-0.5 dark:text-slate-400">Keterangan</label>
                  <input 
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    className="h-7 w-full text-xs border rounded px-2"
                    placeholder="Deskripsi"
                  />
                </div>
              </div>

              {/* Journal Lines - Compact Table */}
              <div>
                <label className="block text-[10px] font-medium text-gray-500 mb-1 dark:text-slate-400">Detail Jurnal</label>
                <div className="border rounded overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-100 dark:bg-gray-800">
                      <tr>
                        <th className="text-left px-2 py-1.5 text-[10px] font-medium">Akun</th>
                        <th className="text-right px-2 py-1.5 text-[10px] font-medium w-24">Debit</th>
                        <th className="text-right px-2 py-1.5 text-[10px] font-medium w-24">Kredit</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {editForm.lines.map((line, idx) => (
                        <tr key={idx}>
                          <td className="px-2 py-1 text-[10px]">
                            <span className="font-mono text-gray-400">{line.account_code}</span>
                            <span className="ml-1">{line.account_name}</span>
                          </td>
                          <td className="px-1 py-0.5">
                            <input 
                              type="number"
                              value={line.debit}
                              onChange={(e) => updateLineAmount(idx, 'debit', e.target.value)}
                              className="h-6 w-full text-[10px] text-right font-mono border rounded px-1"
                              min="0"
                            />
                          </td>
                          <td className="px-1 py-0.5">
                            <input 
                              type="number"
                              value={line.credit}
                              onChange={(e) => updateLineAmount(idx, 'credit', e.target.value)}
                              className="h-6 w-full text-[10px] text-right font-mono border rounded px-1"
                              min="0"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50 dark:bg-gray-800">
                      <tr className="font-semibold">
                        <td className="px-2 py-1.5 text-[10px]">TOTAL</td>
                        <td className="px-2 py-1.5 text-right text-[10px] font-mono text-blue-600 dark:text-blue-400">
                          {formatCurrency(editForm.lines.reduce((sum, l) => sum + (parseFloat(l.debit) || 0), 0))}
                        </td>
                        <td className="px-2 py-1.5 text-right text-[10px] font-mono text-red-600 dark:text-red-400">
                          {formatCurrency(editForm.lines.reduce((sum, l) => sum + (parseFloat(l.credit) || 0), 0))}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                
                {/* Balance Check - Compact */}
                {(() => {
                  const totalDebit = editForm.lines.reduce((sum, l) => sum + (parseFloat(l.debit) || 0), 0);
                  const totalCredit = editForm.lines.reduce((sum, l) => sum + (parseFloat(l.credit) || 0), 0);
                  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;
                  return (
                    <div className={`mt-1 px-2 py-1 rounded text-[10px] ${isBalanced ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                      {isBalanced ? '✓ Jurnal seimbang' : `✗ Selisih: ${formatCurrency(Math.abs(totalDebit - totalCredit))}`}
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Modal Footer - Compact */}
            <div className="px-3 py-2 border-t flex justify-end gap-2 bg-gray-50 dark:bg-gray-800">
              <button 
                onClick={() => { setShowEditModal(false); setEditingJournal(null); }}
                className="px-3 py-1.5 text-xs border rounded hover:bg-gray-100 dark:hover:bg-slate-700"
              >
                Batal
              </button>
              <button 
                onClick={handleSaveJournal}
                disabled={saving}
                className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
              >
                {saving ? 'Menyimpan...' : 'Simpan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Accounting;
